<template>
  <div class="alerts">
    <div class="toolbar">
      <h2 class="page-title">Alerts</h2>
      <div class="toolbar-spacer" />

      <!-- Org-default webhook: the fallback every alert without an override uses. -->
      <LgTooltip
        :text="
          webhookConfigured
            ? 'The Discord webhook alerts notify by default.'
            : 'No org-default Discord webhook yet — alerts without their own webhook can’t notify.'
        "
        placement="bottom"
      >
        <LgButton
          v-if="isAdmin"
          class="webhook-btn"
          size="sm"
          variant="ghost"
          @click="openWebhookModal"
        >
          <template #icon><Webhook :size="14" aria-hidden="true" /></template>
          Webhook:
          <span :class="webhookConfigured ? 'wh-ok' : 'wh-missing'">
            {{ webhookConfigured ? 'configured' : 'not set' }}
          </span>
        </LgButton>
        <span v-else class="webhook-static">
          <Webhook :size="14" aria-hidden="true" />
          Webhook: {{ webhookConfigured ? 'configured' : 'not set' }}
        </span>
      </LgTooltip>

      <LgButton v-if="isAdmin" variant="primary" size="sm" @click="openCreate">
        <template #icon><Plus :size="14" aria-hidden="true" /></template>
        New alert
      </LgButton>
    </div>

    <div class="body">
      <div v-if="listError" class="list-state">
        <GoblinIcon variant="error" :size="48" />
        <p>Couldn’t load alerts.</p>
        <LgButton size="sm" variant="ghost" @click="loadAlerts">Retry</LgButton>
      </div>

      <div v-else-if="loading && !alerts.length" class="list-state"><p>Loading alerts…</p></div>

      <div v-else-if="!alerts.length" class="list-state">
        <GoblinIcon variant="empty" :size="64" />
        <p class="state-title">No alerts yet</p>
        <p class="state-sub">
          An alert watches one metric and pings Discord when it crosses a threshold — “when the p95
          latency stays above 500 for 2 minutes, tell us.”
        </p>
        <LgButton v-if="isAdmin" variant="primary" @click="openCreate">
          <template #icon><Plus :size="14" aria-hidden="true" /></template>
          Create your first alert
        </LgButton>
        <p v-else class="state-sub">An admin can create one from this page.</p>
      </div>

      <ul v-else class="alert-list">
        <li v-for="a in alerts" :key="a.id" class="alert-row" :class="`is-${a.state}`">
          <div class="row-main">
            <div class="row-head">
              <LgChip class="state-chip" :tone="stateTone(a.state)">
                {{ STATE_LABEL[a.state] }}
              </LgChip>
              <span class="alert-name" :title="a.name">{{ a.name }}</span>
              <LgChip v-if="!a.enabled" class="off-chip" tone="neutral">off</LgChip>
              <LgTooltip
                v-if="a.lastWebhookError"
                :text="`Last webhook delivery failed: ${a.lastWebhookError}`"
                placement="bottom"
              >
                <TriangleAlert class="webhook-warn" :size="14" aria-hidden="true" />
              </LgTooltip>
            </div>
            <p class="alert-summary">{{ summary(a) }}</p>
            <p class="alert-meta">
              <span v-if="a.lastValue !== null">last value {{ fmt(a.lastValue) }}</span>
              <span v-if="a.state !== 'ok' && a.stateSince !== null">
                {{ a.state }} for {{ since(a.stateSince) }}
              </span>
              <span v-if="a.hasWebhookOverride">custom webhook</span>
            </p>
            <p
              v-if="rowNotes[a.id]"
              class="row-note"
              :class="rowNotes[a.id].ok ? 'ok' : 'bad'"
              role="status"
            >
              {{ rowNotes[a.id].text }}
            </p>
          </div>

          <div class="row-actions">
            <LgToggle
              v-if="isAdmin"
              :model-value="a.enabled"
              :label="a.enabled ? `Disable ${a.name}` : `Enable ${a.name}`"
              @update:model-value="toggleEnabled(a)"
            />
            <LgIconButton :label="`History for ${a.name}`" size="sm" @click="openHistory(a)">
              <History :size="16" aria-hidden="true" />
            </LgIconButton>
            <LgMenu
              v-if="isAdmin"
              :items="rowMenuItems"
              align="right"
              @select="onRowMenu(a, $event)"
            >
              <template #trigger>
                <LgIconButton :label="`Actions for ${a.name}`" size="sm">
                  <EllipsisVertical :size="16" aria-hidden="true" />
                </LgIconButton>
              </template>
            </LgMenu>
          </div>
        </li>
      </ul>
    </div>

    <!-- Create / edit -->
    <AlertFormModal
      :open="formOpen"
      :alert="editing"
      :org-webhook-configured="webhookConfigured"
      @close="onFormClose"
      @saved="loadAlerts"
    />

    <!-- Delete confirm -->
    <LgModal :open="deleteTarget !== null" title="Delete alert" @close="deleteTarget = null">
      <p class="confirm-text">
        Delete <strong>{{ deleteTarget?.name }}</strong> for everyone? Its firing history goes with
        it.
      </p>
      <template #footer>
        <LgButton variant="ghost" @click="deleteTarget = null">Cancel</LgButton>
        <LgButton variant="danger" @click="confirmDelete">Delete</LgButton>
      </template>
    </LgModal>

    <!-- Org-default webhook (admin) -->
    <LgModal :open="webhookModal" title="Org-default Discord webhook" @close="webhookModal = false">
      <div class="wh-form">
        <p class="wh-desc">
          Every alert without its own webhook notifies this Discord channel. The saved URL is a
          secret — it’s never shown back, only whether one is set.
        </p>
        <p class="wh-status">
          Currently:
          <strong :class="webhookConfigured ? 'wh-ok' : 'wh-missing'">
            {{ webhookConfigured ? 'configured' : 'not set' }}
          </strong>
        </p>
        <!-- WRITE-ONLY: never prefilled. -->
        <LgInput
          :model-value="webhookDraft"
          type="password"
          autocomplete="off"
          placeholder="https://discord.com/api/webhooks/…"
          aria-label="Org default Discord webhook URL"
          @update:model-value="webhookDraft = $event"
        />
        <p class="wh-hint">Discord webhooks only (https://discord.com/api/webhooks/…).</p>
        <p v-if="webhookError" class="form-error" role="alert">{{ webhookError }}</p>
      </div>
      <template #footer>
        <LgButton
          v-if="webhookConfigured"
          variant="danger"
          :disabled="webhookSaving"
          @click="clearWebhook"
        >
          Clear
        </LgButton>
        <LgButton variant="ghost" @click="webhookModal = false">Cancel</LgButton>
        <LgButton
          variant="primary"
          :disabled="!webhookDraft.trim() || webhookSaving"
          @click="saveWebhook"
        >
          {{ webhookSaving ? 'Saving…' : 'Save' }}
        </LgButton>
      </template>
    </LgModal>

    <!-- Firing history -->
    <LgDrawer
      :open="historyTarget !== null"
      side="right"
      :title="historyTarget ? `History — ${historyTarget.name}` : 'History'"
      @close="historyTarget = null"
    >
      <div class="history">
        <div v-if="eventsLoading" class="history-note">Loading history…</div>
        <div v-else-if="eventsError" class="history-note err">Couldn’t load the history.</div>
        <div v-else-if="!events.length" class="history-empty">
          <GoblinIcon variant="empty" :size="40" />
          <p>This alert hasn’t fired yet. Quiet is good.</p>
        </div>
        <ol v-else class="event-list">
          <li v-for="e in events" :key="e.id" class="event">
            <div class="event-head">
              <LgChip :tone="e.resolvedAt === null ? 'danger' : 'ok'">
                {{ e.resolvedAt === null ? 'still firing' : 'resolved' }}
              </LgChip>
              <span class="event-when" :title="abs(e.firedAt)">
                fired {{ since(e.firedAt) }} ago
              </span>
            </div>
            <p class="event-line"><span class="event-k">fired</span> {{ abs(e.firedAt) }}</p>
            <p v-if="e.resolvedAt !== null" class="event-line">
              <span class="event-k">resolved</span> {{ abs(e.resolvedAt) }} (after
              {{ formatDuration(e.resolvedAt - e.firedAt) }})
            </p>
            <p v-if="e.value !== null" class="event-line">
              <span class="event-k">value</span> {{ fmt(e.value) }}
            </p>
            <p v-if="e.message" class="event-msg">{{ e.message }}</p>
          </li>
        </ol>
      </div>
    </LgDrawer>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import {
  EllipsisVertical,
  History,
  Pencil,
  Plus,
  Send,
  Trash2,
  TriangleAlert,
  Webhook,
} from 'lucide-vue-next';
import { api, ApiError, type Alert, type AlertEvent } from '../api.js';
import {
  STATE_LABEL,
  conditionSummary,
  formatAlertNumber,
  formatDuration,
  isDiscordWebhookUrl,
  stateTone,
} from '../lib/alerting.js';
import { formatRelativeTime } from '../lib/format-metric.js';
import type { MenuItem } from '../ui/LgMenu.vue';
import AlertFormModal from '../components/alerts/AlertFormModal.vue';
import GoblinIcon from '../components/GoblinIcon.vue';
import LgButton from '../ui/LgButton.vue';
import LgIconButton from '../ui/LgIconButton.vue';
import LgChip from '../ui/LgChip.vue';
import LgInput from '../ui/LgInput.vue';
import LgMenu from '../ui/LgMenu.vue';
import LgModal from '../ui/LgModal.vue';
import LgToggle from '../ui/LgToggle.vue';
import LgTooltip from '../ui/LgTooltip.vue';
import LgDrawer from '../ui/LgDrawer.vue';

defineProps<{ isAdmin?: boolean }>();
const emit = defineEmits<{ unauthorized: [] }>();

// Alert state changes on the eval cadence (~30s) — refresh the list to match
// so state badges/last values stay honest without a manual reload.
const POLL_MS = 30_000;

const alerts = ref<Alert[]>([]);
const loading = ref(false);
const listError = ref(false);
const webhookConfigured = ref(false);

/** Per-row transient feedback (test-send results, toggle hints), keyed by id. */
const rowNotes = ref<Record<number, { ok: boolean; text: string }>>({});

function onUnauthorized(e: unknown) {
  if (e instanceof ApiError && e.unauthorized) emit('unauthorized');
}

function fmt(v: number | null): string {
  return formatAlertNumber(v);
}
function since(ts: number): string {
  return formatRelativeTime(ts);
}
function abs(ts: number): string {
  return new Date(ts).toLocaleString();
}
function summary(a: Alert): string {
  return conditionSummary(a);
}

// --- Loading -----------------------------------------------------------------
let listInFlight = false;
async function loadAlerts() {
  if (listInFlight) return;
  listInFlight = true;
  loading.value = alerts.value.length === 0;
  try {
    alerts.value = await api.alerts.list();
    listError.value = false;
  } catch (e) {
    listError.value = true;
    onUnauthorized(e);
  } finally {
    loading.value = false;
    listInFlight = false;
  }
}

async function loadWebhookStatus() {
  try {
    webhookConfigured.value = (await api.alerts.webhookDefault()).configured;
  } catch (e) {
    onUnauthorized(e);
  }
}

// --- Create / edit ---------------------------------------------------------------
const formOpen = ref(false);
const editing = ref<Alert | null>(null);

function openCreate() {
  editing.value = null;
  formOpen.value = true;
}
function openEdit(a: Alert) {
  editing.value = a;
  formOpen.value = true;
}
function onFormClose() {
  formOpen.value = false;
  editing.value = null;
}

// --- Row actions -------------------------------------------------------------------
const rowMenuItems: MenuItem[] = [
  { label: 'Edit', value: 'edit', icon: Pencil },
  { label: 'Send test to Discord', value: 'test', icon: Send },
  { label: 'Delete', value: 'delete', icon: Trash2, danger: true },
];

function onRowMenu(a: Alert, value: string) {
  if (value === 'edit') openEdit(a);
  else if (value === 'test') sendRowTest(a);
  else if (value === 'delete') deleteTarget.value = a;
}

async function sendRowTest(a: Alert) {
  setNote(a.id, { ok: true, text: 'Sending test…' });
  try {
    const res = await api.alerts.test(a.id);
    setNote(
      a.id,
      res.ok
        ? { ok: true, text: `Test delivered — Discord answered HTTP ${String(res.status)}.` }
        : {
            ok: false,
            text:
              res.status !== null
                ? `Discord rejected the test (HTTP ${String(res.status)}).`
                : 'The test webhook never completed (network error or timeout).',
          },
    );
  } catch (e) {
    setNote(a.id, {
      ok: false,
      text: e instanceof ApiError ? e.message : 'Test failed — try again.',
    });
    onUnauthorized(e);
  }
}

let noteTimer: ReturnType<typeof setTimeout> | undefined;
function setNote(id: number, note: { ok: boolean; text: string }) {
  rowNotes.value = { ...rowNotes.value, [id]: note };
  if (noteTimer) clearTimeout(noteTimer);
  noteTimer = setTimeout(() => {
    rowNotes.value = {};
  }, 8000);
}

/**
 * Enable/disable straight from the list. `webhookUrl: undefined` omits the key
 * on the wire, which the server treats as "keep the stored webhook_url
 * unchanged" (keep-if-absent) — so toggling an alert with a custom webhook can
 * never clear its override.
 */
async function toggleEnabled(a: Alert) {
  const next = !a.enabled;
  a.enabled = next; // optimistic
  try {
    await api.alerts.update(a.id, {
      name: a.name,
      metric: a.metric,
      labels: a.labels,
      agg: a.agg,
      operator: a.operator,
      threshold: a.threshold,
      evalWindowMs: a.evalWindowMs,
      forMs: a.forMs,
      cooldownMs: a.cooldownMs,
      webhookUrl: undefined,
      messageTemplate: a.messageTemplate,
      notifyOnResolve: a.notifyOnResolve,
      enabled: next,
    });
  } catch (e) {
    a.enabled = !next; // roll back
    setNote(a.id, { ok: false, text: 'Couldn’t save the change.' });
    onUnauthorized(e);
  }
}

// --- Delete ---------------------------------------------------------------------------
const deleteTarget = ref<Alert | null>(null);

async function confirmDelete() {
  const target = deleteTarget.value;
  if (!target) return;
  deleteTarget.value = null;
  try {
    await api.alerts.delete(target.id);
    alerts.value = alerts.value.filter((a) => a.id !== target.id);
  } catch (e) {
    setNote(target.id, { ok: false, text: 'Couldn’t delete the alert.' });
    onUnauthorized(e);
  }
}

// --- History -----------------------------------------------------------------------------
const historyTarget = ref<Alert | null>(null);
const events = ref<AlertEvent[]>([]);
const eventsLoading = ref(false);
const eventsError = ref(false);

async function openHistory(a: Alert) {
  historyTarget.value = a;
  events.value = [];
  eventsLoading.value = true;
  eventsError.value = false;
  try {
    events.value = await api.alerts.events(a.id);
  } catch (e) {
    eventsError.value = true;
    onUnauthorized(e);
  } finally {
    eventsLoading.value = false;
  }
}

// --- Org-default webhook (admin) --------------------------------------------------------------
const webhookModal = ref(false);
const webhookDraft = ref('');
const webhookError = ref('');
const webhookSaving = ref(false);

function openWebhookModal() {
  webhookDraft.value = '';
  webhookError.value = '';
  webhookModal.value = true;
}

async function saveWebhook() {
  const url = webhookDraft.value.trim();
  if (!url || webhookSaving.value) return;
  if (!isDiscordWebhookUrl(url)) {
    webhookError.value = 'Must be a Discord webhook URL (https://discord.com/api/webhooks/…).';
    return;
  }
  webhookSaving.value = true;
  webhookError.value = '';
  try {
    await api.alerts.setWebhookDefault(url);
    webhookConfigured.value = true;
    webhookModal.value = false;
  } catch (e) {
    webhookError.value = e instanceof ApiError ? e.message : 'Couldn’t save the webhook.';
    onUnauthorized(e);
  } finally {
    webhookSaving.value = false;
  }
}

async function clearWebhook() {
  if (webhookSaving.value) return;
  webhookSaving.value = true;
  webhookError.value = '';
  try {
    await api.alerts.setWebhookDefault(null);
    webhookConfigured.value = false;
    webhookModal.value = false;
  } catch (e) {
    webhookError.value = e instanceof ApiError ? e.message : 'Couldn’t clear the webhook.';
    onUnauthorized(e);
  } finally {
    webhookSaving.value = false;
  }
}

// --- Poll ---------------------------------------------------------------------------------------
let pollTimer: ReturnType<typeof setInterval> | undefined;

onMounted(() => {
  loadAlerts();
  loadWebhookStatus();
  pollTimer = setInterval(loadAlerts, POLL_MS);
});
onBeforeUnmount(() => {
  if (pollTimer) clearInterval(pollTimer);
  if (noteTimer) clearTimeout(noteTimer);
});
</script>

<style scoped>
.alerts {
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
}
.toolbar {
  display: flex;
  align-items: center;
  gap: var(--lg-space-2);
  flex-wrap: wrap;
  padding: var(--lg-space-2) var(--lg-space-3);
  border-bottom: 1px solid var(--lg-border);
  flex-shrink: 0;
}
.toolbar-spacer {
  flex: 1;
  min-width: 0;
}
.page-title {
  margin: 0;
  font-size: var(--lg-fs-md);
  font-weight: 600;
}
.webhook-static {
  display: inline-flex;
  align-items: center;
  gap: var(--lg-space-1);
  font-size: var(--lg-fs-sm);
  color: var(--lg-text-dim);
}
.wh-ok {
  color: var(--lg-ok);
}
.wh-missing {
  color: var(--lg-warn);
}

.body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: var(--lg-space-3);
}
.list-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--lg-space-2);
  padding: var(--lg-space-8) var(--lg-space-4);
  color: var(--lg-text-faint);
  text-align: center;
}
.state-title {
  margin: 0;
  font-size: var(--lg-fs-lg);
  color: var(--lg-text-dim);
}
.state-sub {
  margin: 0;
  font-size: var(--lg-fs-sm);
  max-width: 52ch;
}

.alert-list {
  list-style: none;
  margin: 0 auto;
  padding: 0;
  max-width: 860px;
  display: flex;
  flex-direction: column;
  gap: var(--lg-space-2);
}
.alert-row {
  display: flex;
  align-items: flex-start;
  gap: var(--lg-space-3);
  border: 1px solid var(--lg-border);
  border-radius: var(--lg-radius-lg);
  background: var(--lg-bg-surface);
  padding: var(--lg-space-3);
  min-width: 0;
}
.alert-row.is-firing {
  border-color: color-mix(in srgb, var(--lg-danger) 55%, var(--lg-border));
}
.alert-row.is-pending {
  border-color: color-mix(in srgb, var(--lg-warn) 45%, var(--lg-border));
}
.row-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--lg-space-1);
}
.row-head {
  display: flex;
  align-items: center;
  gap: var(--lg-space-2);
  min-width: 0;
  flex-wrap: wrap;
}
.alert-name {
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
.off-chip {
  opacity: 0.8;
}
.webhook-warn {
  color: var(--lg-warn);
  flex-shrink: 0;
}
.alert-summary {
  margin: 0;
  font-family: var(--lg-font-mono);
  font-size: var(--lg-fs-sm);
  color: var(--lg-text-dim);
  overflow-wrap: anywhere;
}
.alert-meta {
  margin: 0;
  display: flex;
  gap: var(--lg-space-3);
  flex-wrap: wrap;
  font-size: var(--lg-fs-xs);
  color: var(--lg-text-faint);
}
.row-note {
  margin: 0;
  font-size: var(--lg-fs-sm);
}
.row-note.ok {
  color: var(--lg-ok);
}
.row-note.bad {
  color: var(--lg-danger);
}
.row-actions {
  display: flex;
  align-items: center;
  gap: var(--lg-space-2);
  flex-shrink: 0;
}

.confirm-text {
  margin: 0;
  font-size: var(--lg-fs-sm);
  color: var(--lg-text-dim);
}
.form-error {
  margin: 0;
  font-size: var(--lg-fs-sm);
  color: var(--lg-danger);
}

.wh-form {
  display: flex;
  flex-direction: column;
  gap: var(--lg-space-3);
}
.wh-desc,
.wh-status {
  margin: 0;
  font-size: var(--lg-fs-sm);
  color: var(--lg-text-dim);
}
.wh-hint {
  margin: 0;
  font-size: var(--lg-fs-xs);
  color: var(--lg-text-faint);
}

.history {
  display: flex;
  flex-direction: column;
  gap: var(--lg-space-3);
}
.history-note {
  color: var(--lg-text-faint);
  font-size: var(--lg-fs-sm);
}
.history-note.err {
  color: var(--lg-danger);
}
.history-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--lg-space-2);
  padding: var(--lg-space-6) var(--lg-space-3);
  color: var(--lg-text-faint);
  text-align: center;
}
.history-empty p {
  margin: 0;
  font-size: var(--lg-fs-sm);
}
.event-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--lg-space-3);
}
.event {
  border: 1px solid var(--lg-border);
  border-radius: var(--lg-radius-md);
  padding: var(--lg-space-3);
  display: flex;
  flex-direction: column;
  gap: var(--lg-space-1);
  background: var(--lg-bg-surface);
}
.event-head {
  display: flex;
  align-items: center;
  gap: var(--lg-space-2);
  flex-wrap: wrap;
}
.event-when {
  font-size: var(--lg-fs-xs);
  color: var(--lg-text-faint);
}
.event-line {
  margin: 0;
  font-size: var(--lg-fs-sm);
  color: var(--lg-text-dim);
}
.event-k {
  display: inline-block;
  min-width: 64px;
  font-size: var(--lg-fs-xs);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--lg-text-faint);
}
.event-msg {
  margin: 0;
  font-family: var(--lg-font-mono);
  font-size: var(--lg-fs-sm);
  color: var(--lg-text);
  overflow-wrap: anywhere;
}

@media (max-width: 768px) {
  .body {
    padding: var(--lg-space-2);
  }
  .alert-row {
    flex-direction: column;
  }
  .row-actions {
    align-self: flex-end;
  }
}
</style>
